import { useState } from 'react'
import { Pencil, ExternalLink, AlertCircle } from 'lucide-react'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { Textarea } from '../../lib/shadcn/textarea'
import { Spinner } from '../../lib/shadcn/spinner'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { toast } from '../../lib/shadcn/sonner'
import { Chip } from '../interviews/Chip'
import { useUpdateIncident } from '../../hooks/backend/fleet'
import { writeErrorMessage } from '../../utils/writeError'
import { formatDate } from '../../utils/fleet'
import {
  INCIDENT_STATUSES, type IncidentRow, incidentStatusLabel, incidentTypeLabel,
  incidentTypeTone, urgencyTone, zarExact,
} from '../../utils/servicing'

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  if (children === null || children === undefined || children === '') return null
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-xs text-foreground">{children}</p>
    </div>
  )
}

const YES_NO = (v: boolean | null | undefined) => (v === null || v === undefined ? null : v ? 'Yes' : 'No')

export function IncidentDetailDialog({
  row, canEdit, onSaved,
}: { row: IncidentRow; canEdit: boolean; onSaved: () => void }) {
  const [open, setOpen] = useState(false)
  const [status, setStatus] = useState(row.status)
  const [outcome, setOutcome] = useState(row.outcome ?? '')
  const [cost, setCost] = useState(row.cost_zar === null ? '' : String(row.cost_zar))
  const [notes, setNotes] = useState(row.resolution_notes ?? '')
  // Fault is held as a tri-state string, not a boolean, so "not determined" is a
  // real value the operator can leave alone. A checkbox would force every
  // incident opened for any reason into a fault finding.
  const [fault, setFault] = useState<'unset' | 'yes' | 'no'>(
    row.at_fault === null || row.at_fault === undefined ? 'unset' : row.at_fault ? 'yes' : 'no',
  )
  const [faultBasis, setFaultBasis] = useState(row.fault_basis ?? '')
  const update = useUpdateIncident()

  const faultChanged =
    (fault === 'unset' ? null : fault === 'yes') !== (row.at_fault ?? null)

  async function save() {
    const patch: Record<string, unknown> = {}
    if (status !== row.status) patch.status = status
    if ((outcome || null) !== (row.outcome ?? null)) patch.outcome = outcome || null
    if ((cost === '' ? null : Number(cost)) !== (row.cost_zar ?? null)) patch.cost_zar = cost === '' ? null : Number(cost)
    if ((notes || null) !== (row.resolution_notes ?? null)) patch.resolution_notes = notes || null
    if (faultChanged) {
      patch.at_fault = fault === 'unset' ? null : fault === 'yes'
      patch.fault_basis = faultBasis || null
    }
    if (Object.keys(patch).length === 0) {
      toast.info('Nothing changed')
      return
    }
    try {
      await update.trigger({ incident_id: row.incident_id, patch }).result
      toast.success('Incident updated')
      setOpen(false)
      onSaved()
    } catch (err) {
      toast.error('Could not update the incident', { description: writeErrorMessage(err) })
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="h-7 gap-1.5 px-2 text-xs">
          <Pencil className="h-3.5 w-3.5" />
          {canEdit ? 'Open' : 'View'}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            <span className="font-mono uppercase">{row.registration_number ?? 'No vehicle'}</span>
            <Chip tone={incidentTypeTone(row.incident_type)}>{incidentTypeLabel(row.incident_type)}</Chip>
            {row.sub_type && <Chip tone="neutral">{row.sub_type}</Chip>}
          </DialogTitle>
          <DialogDescription>
            {row.occurred_at ? `Occurred ${formatDate(row.occurred_at)}` : 'No incident date recorded'}
            {row.reported_by_name ? `, reported by ${row.reported_by_name}` : ''}
            {row.intake_source ? ` (${row.intake_source.replace(/_/g, ' ')})` : ''}
          </DialogDescription>
        </DialogHeader>

        {!row.vehicle_id && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              This report did not match any registration in the fleet register, so it is not attached
              to a vehicle and will not appear on that vehicle's history. Correct the vehicle on the
              record to attach it.
            </span>
          </div>
        )}

        {row.description && (
          <div className="rounded-md border border-border bg-background p-3">
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              What was reported
            </p>
            <p className="whitespace-pre-wrap text-xs text-foreground">{row.description}</p>
          </div>
        )}

        {row.immediate_action && (
          <div className="rounded-md border border-border bg-background p-3">
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Immediate action taken
            </p>
            <p className="whitespace-pre-wrap text-xs text-foreground">{row.immediate_action}</p>
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Owner">{row.owner_name}</Field>
          <Field label="Driver">
            {row.driver_name}
            {row.driver_unmatched ? ' (name only, no driver record matched)' : ''}
          </Field>
          <Field label="Urgency">
            {row.urgency ? <Chip tone={urgencyTone(row.urgency)}>{row.urgency}</Chip> : null}
          </Field>
          <Field label="Severity">{row.severity ?? 'Not assessed'}</Field>
          <Field label="Drivable">{YES_NO(row.drivable)}</Field>
          <Field label="Operational when reported">{YES_NO(row.operational)}</Field>
          <Field label="Police attended">{YES_NO(row.police_attended)}</Field>
          <Field label="Case number">{row.case_number}</Field>
          <Field label="Odometer">{row.odometer_km ? `${row.odometer_km.toLocaleString('en-ZA')} km` : null}</Field>
          <Field label="Where it happened">{row.location_text}</Field>
          <Field label="Vehicle location">{row.current_location}</Field>
          <Field label="Who was informed">{row.informed}</Field>
          <Field label="Estimated cost">
            {row.estimated_cost_zar ? zarExact(row.estimated_cost_zar) : null}
          </Field>
          <Field label="Daily storage">
            {row.daily_storage_cost_zar ? zarExact(row.daily_storage_cost_zar) : null}
          </Field>
          <Field label="In charge">{row.in_charge_name ?? row.assigned_to}</Field>
        </div>

        {row.evidence_urls && row.evidence_urls.length > 0 && (
          <div>
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Evidence ({row.evidence_urls.length})
            </p>
            <ul className="space-y-1">
              {row.evidence_urls.map((u, i) => (
                <li key={u}>
                  <a
                    href={u}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-[hsl(var(--brand))] underline"
                  >
                    <ExternalLink className="h-3 w-3" />
                    {u.split('/').pop() || `File ${i + 1}`}
                  </a>
                </li>
              ))}
            </ul>
            <p className="mt-1 text-[11px] text-muted-foreground">
              These are Typeform links and need a Typeform session to open. Mirroring them into
              Supabase Storage is still outstanding.
            </p>
          </div>
        )}

        {canEdit && (
          <div className="space-y-3 rounded-md border border-border p-3">
            <p className="text-xs font-semibold text-foreground">Update this incident</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor={`st-${row.incident_id}`}>Status</Label>
                <NativeSelect id={`st-${row.incident_id}`} value={status} onChange={(e) => setStatus(e.target.value)}>
                  {INCIDENT_STATUSES.map((s) => (
                    <option key={s.code} value={s.code}>{s.label}</option>
                  ))}
                </NativeSelect>
              </div>
              {/* Read-only on purpose. The next update date moves only when an
                  update is posted (update_incident_v1 refuses a later date from
                  here, migration 1600), otherwise pushing it out a week at a time
                  would keep an incident on track forever with nothing posted. */}
              <div className="space-y-1.5">
                <Label>Next update due</Label>
                <p className="text-xs text-foreground">
                  {row.next_update_date ? formatDate(row.next_update_date) : 'Not set'}
                  {row.in_charge_name ? `, ${row.in_charge_name} in charge` : ', nobody in charge'}
                </p>
                <p className="text-[11px] text-muted-foreground">
                  Set by posting an update on the Timeline.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`oc-${row.incident_id}`}>Outcome</Label>
                <Input id={`oc-${row.incident_id}`} value={outcome}
                  onChange={(e) => setOutcome(e.target.value)} placeholder="Repaired, claim paid, written off" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`co-${row.incident_id}`}>Actual cost (R)</Label>
                <Input id={`co-${row.incident_id}`} type="number" inputMode="decimal" value={cost}
                  onChange={(e) => setCost(e.target.value)} />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor={`fa-${row.incident_id}`}>Driver at fault</Label>
              <NativeSelect id={`fa-${row.incident_id}`} value={fault}
                onChange={(e) => setFault(e.target.value as 'unset' | 'yes' | 'no')}>
                <option value="unset">Not determined</option>
                <option value="yes">Yes, driver at fault</option>
                <option value="no">No, not the driver</option>
              </NativeSelect>
              {fault !== 'unset' && (
                <>
                  <Label htmlFor={`fb-${row.incident_id}`}>Basis for that finding</Label>
                  <Textarea id={`fb-${row.incident_id}`} rows={2} value={faultBasis}
                    onChange={(e) => setFaultBasis(e.target.value)}
                    placeholder="Dashcam footage, third party statement, assessor report" />
                  <p className="text-[11px] text-muted-foreground">
                    A fault finding can put a repair bill on a driver's account, so the basis is
                    required and the database records who decided and when.
                  </p>
                </>
              )}
              {row.fault_determined_by_name && (
                <p className="text-[11px] text-muted-foreground">
                  Currently: {row.at_fault ? 'driver at fault' : 'not the driver'}, decided by{' '}
                  {row.fault_determined_by_name}
                  {row.fault_determined_at ? ` on ${formatDate(row.fault_determined_at)}` : ''}.
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor={`rn-${row.incident_id}`}>Resolution notes</Label>
              <Textarea id={`rn-${row.incident_id}`} rows={3} value={notes}
                onChange={(e) => setNotes(e.target.value)} />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={update.loading}>Close</Button>
          {canEdit && (
            <Button onClick={save} disabled={update.loading}>
              {update.loading && <Spinner className="mr-2 h-4 w-4" />}
              Save
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
